import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { BlockInstance } from '@sytel/blocks';

export interface SeoFields {
  title?: string;
  description?: string;
  keywords?: string;
}

export interface OpenGraphFields {
  title?: string;
  description?: string;
  image?: string;
  type?: 'website' | 'article';
}

export interface PageAdvanced {
  scheduled_at: string | null;
  canonical_url: string | null;
  preview_image: string | null;
  no_index: boolean;
  no_follow: boolean;
  schema_data: Record<string, unknown> | Array<Record<string, unknown>> | null;
  description: string | null;
}

interface PageEditorState {
  pageId: string | null;
  title: string;
  slug: string;
  locale: string;
  status: 'draft' | 'published' | 'archived' | 'scheduled';
  blocks: BlockInstance[];
  selectedBlockId: string | null;
  isDirty: boolean;
  seo: SeoFields;
  openGraph: OpenGraphFields;
  advanced: PageAdvanced;
  init: (data: {
    pageId: string | null;
    title: string;
    slug: string;
    locale: string;
    status: 'draft' | 'published' | 'archived' | 'scheduled';
    blocks: BlockInstance[];
    seo?: SeoFields;
    openGraph?: OpenGraphFields;
    advanced?: Partial<PageAdvanced>;
  }) => void;
  setMeta: (meta: Partial<{ title: string; slug: string; locale: string; status: PageEditorState['status'] }>) => void;
  setSeo: (seo: SeoFields) => void;
  setOpenGraph: (og: OpenGraphFields) => void;
  setAdvanced: (a: Partial<PageAdvanced>) => void;
  addBlock: (block: BlockInstance) => void;
  insertBlock: (block: BlockInstance, index: number) => void;
  duplicateBlock: (id: string) => void;
  toggleBlockVisible: (id: string) => void;
  updateBlockData: (id: string, data: unknown) => void;
  removeBlock: (id: string) => void;
  reorder: (sourceIndex: number, destIndex: number) => void;
  selectBlock: (id: string | null) => void;
  clearDirty: () => void;
}

const DEFAULT_ADVANCED: PageAdvanced = {
  scheduled_at: null,
  canonical_url: null,
  preview_image: null,
  no_index: false,
  no_follow: false,
  schema_data: null,
  description: null,
};

export const usePageEditor = create<PageEditorState>()(
  immer((set) => ({
    pageId: null,
    title: '',
    slug: '',
    locale: 'es',
    status: 'draft',
    blocks: [],
    selectedBlockId: null,
    isDirty: false,
    seo: {},
    openGraph: {},
    advanced: { ...DEFAULT_ADVANCED },
    init: (data) =>
      set((state) => {
        state.pageId = data.pageId;
        state.title = data.title;
        state.slug = data.slug;
        state.locale = data.locale;
        state.status = data.status;
        state.blocks = data.blocks;
        state.selectedBlockId = null;
        state.isDirty = false;
        state.seo = data.seo ?? {};
        state.openGraph = data.openGraph ?? {};
        state.advanced = { ...DEFAULT_ADVANCED, ...(data.advanced ?? {}) };
      }),
    setMeta: (meta) =>
      set((state) => {
        Object.assign(state, meta);
        state.isDirty = true;
      }),
    setSeo: (seo) =>
      set((state) => {
        state.seo = seo;
        state.isDirty = true;
      }),
    setOpenGraph: (og) =>
      set((state) => {
        state.openGraph = og;
        state.isDirty = true;
      }),
    setAdvanced: (a) =>
      set((state) => {
        state.advanced = { ...state.advanced, ...a };
        state.isDirty = true;
      }),
    addBlock: (block) =>
      set((state) => {
        state.blocks.push(block);
        state.selectedBlockId = block.id;
        state.isDirty = true;
      }),
    insertBlock: (block, index) =>
      set((state) => {
        const clamped = Math.max(0, Math.min(index, state.blocks.length));
        state.blocks.splice(clamped, 0, block);
        state.blocks.forEach((b, i) => { b.order = i; });
        state.selectedBlockId = block.id;
        state.isDirty = true;
      }),
    duplicateBlock: (id) =>
      set((state) => {
        const idx = state.blocks.findIndex((b) => b.id === id);
        if (idx < 0) return;
        const src = state.blocks[idx];
        if (!src) return;
        const copy: BlockInstance = {
          ...src,
          id: typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${src.id}-copy-${Date.now()}`,
          data: structuredClone(src.data),
        };
        state.blocks.splice(idx + 1, 0, copy);
        state.blocks.forEach((b, i) => { b.order = i; });
        state.selectedBlockId = copy.id;
        state.isDirty = true;
      }),
    toggleBlockVisible: (id) =>
      set((state) => {
        const block = state.blocks.find((b) => b.id === id);
        if (block) {
          block.visible = !block.visible;
          state.isDirty = true;
        }
      }),
    updateBlockData: (id, data) =>
      set((state) => {
        const block = state.blocks.find((b) => b.id === id);
        if (block) {
          block.data = data as Record<string, unknown>;
          state.isDirty = true;
        }
      }),
    removeBlock: (id) =>
      set((state) => {
        state.blocks = state.blocks.filter((b) => b.id !== id);
        if (state.selectedBlockId === id) state.selectedBlockId = null;
        state.isDirty = true;
      }),
    reorder: (sourceIndex, destIndex) =>
      set((state) => {
        const [moved] = state.blocks.splice(sourceIndex, 1);
        if (moved) state.blocks.splice(destIndex, 0, moved);
        state.blocks.forEach((b, i) => {
          b.order = i;
        });
        state.isDirty = true;
      }),
    selectBlock: (id) =>
      set((state) => {
        state.selectedBlockId = id;
      }),
    clearDirty: () =>
      set((state) => {
        state.isDirty = false;
      }),
  })),
);
